<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="700px"
    title-padding="pb-0"
  >
    <!--<editor-fold desc="Dialog Header">-->
    <template slot="dialogTitle">
      <div>
        <span v-if="roleDirty.id">
          Edit Role
        </span>
        <span v-else>
          Add New Role
        </span>
      </div>
      <div>
        <v-tabs
          v-model="tab"
          background-color="transparent"
        >
          <v-tab class="text-none">
            Basic & Permissions
          </v-tab>
          <v-tab class="text-none">
            Notifications
          </v-tab>
        </v-tabs>
      </div>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-tabs-items v-model="tab">
        <v-tab-item eager>
          <v-subheader>
            Basic Details
          </v-subheader>
          <v-text-field
            v-model="roleDirty.name"
            outlined
            label="Name"
            placeholder="Name"
          />
          <v-text-field
            v-model="roleDirty.title"
            outlined
            hide-details
            label="Title"
            placeholder="Title"
          />

          <v-switch
            v-model="allowAllAbilities"
            label="Activate all permissions"
          />

          <div v-if="!allowAllAbilities">
            <v-subheader>
              Visibility of other staff (mostly Service module)
            </v-subheader>
            <v-autocomplete
              v-model="rolesVisible"
              :items="roles.data"
              :loading="roles.loading"
              outlined
              multiple
              small-chips
              label="Select Visible Roles"
              item-value="id"
              item-text="title"
            />

            <v-subheader>
              Roles that can be selected for a user (in case of Staff create/edit possibility)
            </v-subheader>
            <v-autocomplete
              v-model="rolesSettable"
              :items="roles.data"
              :loading="roles.loading"
              outlined
              multiple
              small-chips
              label="Select Settable Roles"
              item-value="id"
              item-text="title"
            />

            <v-subheader>
              Permissions
            </v-subheader>

            <div>
              <v-row
                v-for="ability in roleAbilities"
                :key="ability.id"
                no-gutters
                align="center"
                class="list--bordered py-2"
              >
                <v-col>
                  {{ ability.title }}
                </v-col>
                <v-col cols="auto">
                  <v-switch
                    v-model="ability.value"
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </v-tab-item>
        <v-tab-item eager>
          <div>
            <v-row
              v-for="ability in notificationAbilities"
              :key="ability.id"
              no-gutters
              align="center"
              class="list--bordered py-2"
            >
              <v-col>
                {{ ability.title }}
              </v-col>
              <v-col cols="auto">
                <v-switch
                  v-model="ability.value"
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import Role from '@/models/Role'
  import Ability from '@/models/Ability'
  import ContentDialog from '@/components/helpers/Dialog'

  export default {
    name: 'RoleEditDialog',
    components: {
      ContentDialog
    },
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      role: {
        type: Object,
        default: () => Role.emptyModel
      }
    },
    data() {
      return {
        roleDirty: cloneDeep(Role.emptyModel),
        roles: {
          data: [],
          loading: false
        },
        abilities: {
          data: [],
          notifications: [],
          loading: false
        },
        roleAbilities: [],
        notificationAbilities: [],
        rolesSettable: [],
        rolesVisible: [],
        allowAllAbilities: false,
        submitting: false,
        tab: null
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      }
    },
    watch: {
      role: function () {
        this.roleDirty = cloneDeep(this.role)
        this.prepareAbilities()
      }
    },
    created() {
      this.fetchRoles()
      this.fetchAbilities()
    },
    methods: {
      fetchRoles: async function() {
        this.roles.loading = true
        try {
          this.roles.data = await Role.api.fetch()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.roles.loading = false
        }
      },
      fetchAbilities: async function () {
        this.abilities.loading = true

        try {
          const abilities = await Ability.api.fetch()

          this.abilities.data = abilities.filter(a => !a.name.includes('notification.'))
          this.abilities.notifications = abilities.filter(a => a.name.includes('notification.'))

          this.prepareAbilities()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.abilities.loading = false
        }
      },
      submit: async function () {
        let result = await this.$validator.validate()

        if (!result) return

        this.save()
      },
      save: async function () {
        this.submitting = true

        const data = this.preparePostData()

        try {
          let role = await Role.api.save(data, this.roleDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Role saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.roleDirty.id ? 'role:updated' : 'role:created', role)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      canSetRoles: function () {
        const editStaffAbility = this.roleAbilities.find(ability => ability.name === 'edit' && ability.entity_type === 'App\\Staff')
        const createStaffAbility = this.roleAbilities.find(ability => ability.name === 'create' && ability.entity_type === 'App\\Staff')

        return (editStaffAbility && editStaffAbility.value) || (createStaffAbility && createStaffAbility.value)
      },
      prepareNotificationsPostData: function () {
        return this.notificationAbilities
      },
      preparePostData: function () {
        if (this.allowAllAbilities) {
          return {
            name: this.roleDirty.name,
            title: this.roleDirty.title,
            abilities: this.abilities.data.filter(ability => this.findAllowAllAbilityCondition(ability)).map(a => a.id),
            notifications: this.prepareNotificationsPostData()
          }
        }
        let abilities = []

        // add visible roles abilities
        let rolesVisibleAbilities = this.abilities.data.filter(ability => ability.name === 'view' && ability.entity_type === 'roles' && this.rolesVisible.indexOf(ability.entity_id) > -1)
        abilities.push(...rolesVisibleAbilities.map(a => a.id))

        // add settable roles abilities
        if (this.canSetRoles) {
          let rolesSettable = this.abilities.data.filter(ability => ability.name === 'set' && ability.entity_type === 'roles' && this.rolesSettable.indexOf(ability.entity_id) > -1)
          abilities.push(...rolesSettable.map(a => a.id))
        }

        // add other abilities
        abilities.push(...this.roleAbilities.filter(a => a.value).map(a => a.id))

        return {
          name: this.roleDirty.name,
          title: this.roleDirty.title,
          abilities: abilities,
          notifications: this.prepareNotificationsPostData()
        }
      },
      prepareAbilities: function () {
        const settableRoleAbilityCondition = ability => ability.name === 'set' && ability.entity_type === 'roles' && ability.entity_id
        const visibleRoleAbilityCondition = ability => ability.name === 'view' && ability.entity_type === 'roles' && ability.entity_id
        const serviceAllowLocationAbilityCondition = ability => ability.name === 'service_allow_base' || ability.name === 'service_allow_country'

        // find abilities for settable roles
        this.rolesSettable = this.roleDirty.abilities.filter(ability => settableRoleAbilityCondition(ability))
          .map(ability => ability.entity_id)

        // find abilities for visible roles
        this.rolesVisible = this.roleDirty.abilities.filter(ability => visibleRoleAbilityCondition(ability))
          .map(ability => ability.entity_id)

        // find allow all abilities ability
        this.allowAllAbilities = !!this.roleDirty.abilities.find(ability => this.findAllowAllAbilityCondition(ability))

        // remove visible&settable roles abilities + service locations abilities
        this.roleAbilities = this.abilities.data.filter(ability => {
          return !settableRoleAbilityCondition(ability) &&
            !visibleRoleAbilityCondition(ability) &&
            !serviceAllowLocationAbilityCondition(ability) &&
            !this.findAllowAllAbilityCondition(ability)
        })
          .map(ability => ({ ...ability, value: !!this.roleDirty.abilities.find(a => a.id === ability.id) }))

        this.notificationAbilities = this.abilities.notifications.map(ability => ({ ...ability, value: !!this.roleDirty.abilities.find(a => a.id === ability.id) }))
      },
      findAllowAllAbilityCondition: function (ability) {
        return ability.name === '*' && ability.entity_type === '*'
      }
    }
  }
</script>
